import React, { useEffect, useState, useRef } from "react";
import {
  Card,
  Tabs,
  Table,
  Upload,
  Button,
  Typography,
  Modal,
  Space,
  Divider,
} from "antd";
import {
  UploadOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchPatientDetails } from "../../Redux/Slice/Doctor/Patients";
import { fetchDocument, uploadDoc } from "../../Redux/Slice/Patient/uploadDoc";
import { toast } from "react-toastify";
import VitalsTab from "./VitalTab";
import DocumentTable from "./Documents";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const PatientRecord = () => {
  const dispatch = useDispatch();
  const [summaryModal, setSummaryModal] = useState(false);
  const printRef = useRef();

  const user = JSON.parse(localStorage.getItem("user"));
  const patientId = user?.patientId;

  const { data, loading } = useSelector(
    (state) => state.patient.patientDetails
  );
  const { data: documentData } = useSelector((state) => state.upload.fetch);

  useEffect(() => {
    dispatch(fetchPatientDetails(patientId));
  }, [dispatch, patientId]);

  const patient = data?.data?.[0] || {};

  useEffect(() => {
    dispatch(fetchDocument({ patientId }));
  }, [dispatch, patientId]);

  const handleDownload = () => {
    const content = printRef.current;
    const printWindow = window.open("", "", "width=1000,height=800");
    printWindow.document.write("<html><head><title>Summary Report</title>");
    printWindow.document.write(
      "<style>body{font-family:Arial;padding:20px;} table{border-collapse:collapse;width:100%;} table, th, td{border:1px solid #ddd;padding:8px;} th{background:#f5f5f5;}</style>"
    );
    printWindow.document.write("</head><body >");
    printWindow.document.write(content.innerHTML);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <>
      <Card
        title={<Title level={4}>Patient Records</Title>}
        extra={
          <Space>
            {/* Upload Button */}
            <Upload
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  const res = await dispatch(
                    uploadDoc({ patientId, file })
                  ).unwrap();
                  if (res) {
                    toast.success(`${file.name} uploaded successfully`);
                    dispatch(fetchDocument({ patientId }));
                  }
                  onSuccess("ok");
                } catch (err) {
                  console.log("err", err);

                  toast.error(err?.message || "Upload failed");
                  onError(err);
                }
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>

            {/* EMR Button */}
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={() => setSummaryModal(true)}
            >
              EMR
            </Button>
          </Space>
        }
        style={{ margin: "0 auto" }}
        loading={loading}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Vitals" key="2">
            <VitalsTab vitals={patient?.vitals || []} />
          </TabPane>

          <TabPane tab="EMR" key="3">
            <Table
              columns={[
                { title: "Chief Complaint", dataIndex: "chiefComplaint" },
                { title: "Diagnosis", dataIndex: "diagnosis" },
                { title: "Created On", dataIndex: "createdOn" },
              ]}
              dataSource={patient.emrs || []}
              rowKey={(r, i) => i}
              pagination={false}
              bordered
            />
          </TabPane>

          <TabPane tab="Documents" key="4">
            <DocumentTable
              documentData={documentData || []}
              patientId={patientId}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* Summary Report Modal */}
      <Modal
        open={summaryModal}
        style={{ top: 20 }}
        title={
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Title level={4} style={{ margin: 0 }}>
              Summary Report
            </Title>
            <Button
              type="default"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Space>
        }
        onCancel={() => setSummaryModal(false)}
        footer={null}
        width={1100}
        styles={{ body: { maxHeight: "75vh", overflowY: "auto", padding: 24 } }}
      >
        <div ref={printRef}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <Title level={3} style={{ margin: 0 }}>
              ABC Multispeciality Hospital
            </Title>
            <Text>123 Health Street, Chennai, India</Text>
            <Divider />
          </div>

          {/* Patient Info */}
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>Patient Details</Title>
            <p>
              <b>Name:</b> {patient.demographics?.name}
            </p>
            <p>
              <b>Age/Gender:</b> {patient.demographics?.age} /{" "}
              {patient.demographics?.gender}
            </p>
            <p>
              <b>Email:</b> {patient.demographics?.emailId}
            </p>
            <p>
              <b>Phone:</b> {patient.demographics?.phoneNo}
            </p>
            <p>
              <b>Address:</b> {patient.demographics?.address}
            </p>
            <Divider />
          </div>

          {/* EMR Records */}
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>EMR Records</Title>
            <Table
              columns={[
                { title: "S.No", render: (_, __, i) => i + 1 },
                { title: "Chief Complaint", dataIndex: "chiefComplaint" },
                { title: "Diagnosis", dataIndex: "diagnosis" },
                { title: "Created On", dataIndex: "createdOn" },
              ]}
              dataSource={patient.emrs || []}
              rowKey={(r, i) => i}
              pagination={false}
              bordered
              size="small"
            />
            <Divider />
          </div>

          {/* Prescriptions */}
          <div style={{ marginBottom: 24 }}>
            <Title level={4}>Prescriptions</Title>
            <Table
              columns={[
                { title: "Medicine", dataIndex: "medicineName" },
                { title: "Dosage", dataIndex: "dosage" },
                { title: "Qty", dataIndex: "quantity" },
                { title: "Frequency", dataIndex: "frequency" },
                { title: "Instruction", dataIndex: "foodInstruction" },
              ]}
              dataSource={patient.prescriptions || []}
              rowKey={(r, i) => i}
              pagination={false}
              bordered
              size="small"
            />
            <Divider />
          </div>

          {/* Prescription PDFs */}
          {/* <div>
            <Title level={4}>Prescription PDFs</Title>
            {(patient.prescriptionPdfs || []).map((pdf, idx) => (
              <p key={idx}>
                <a href={pdf.pdfUrl} target="_blank" rel="noopener noreferrer">
                  View Prescription ({pdf.createdOn})
                </a>
              </p>
            ))}
          </div> */}
        </div>
      </Modal>
    </>
  );
};

export default PatientRecord;
